import { enviroment } from "../../enviroment";

import { useAuthHttpClient } from "./useAuthHttpClient";
import { TemplateProfile } from "../../core/entities/templateProfile";
import { TemplateTypeDisplay } from "../../core/entities/templateTypeDisplay";
import { TemplateProfileDto } from "../dtos/templateProfileDto";
import { TemplateTypeDto } from "../dtos/templateTypeDto";
import { Template, TemplateType } from "../../core/entities/template";
import { ReplacementField } from "../../core/entities/replacementField";
import { TemplateDto } from "../dtos/templateDto";
import { mapDtoToTemplate } from "../dtos/mappers/mapDtoToTemplate";
import { mapDtoToTemplateProfile } from "../dtos/mappers/mapDtoToTemplateProfile";
import { mapDtoToTemplateTypes } from "../dtos/mappers/mapDtoToTemplateTypes";
import { throwIfNotSuccess } from "./throwIfNotSuccess";

const API_URL = `${enviroment.apiUrl}/api/v1/templates`;

export function useTemplateService(): TemplateService {
    const { authFetch } = useAuthHttpClient();

    async function getReplacementFields(): Promise<ReplacementField[]> {
        const url = `${API_URL}/options/replacement-fields`;
        const response = await authFetch(url);
        throwIfNotSuccess(response);
        return await response.json();
    }

    async function getTypes(): Promise<TemplateTypeDisplay[]> {
        const url = `${API_URL}/options/types`;
        const response = await authFetch(url);
        throwIfNotSuccess(response);
        const dtos: TemplateTypeDto[] = await response.json();
        return dtos.map(mapDtoToTemplateTypes);
    }

    async function getAllTemplateProfiles(): Promise<TemplateProfile[]> {
        const url = `${API_URL}`;
        const response = await authFetch(url);
        throwIfNotSuccess(response);
        const dtos: TemplateProfileDto[] = await response.json();
        return dtos.map((x) => mapDtoToTemplateProfile(x));
    }

    async function getPublishedWithReplacedFields(): Promise<Template[]> {
        const url = `${API_URL}/published/filled`;
        const response = await authFetch(url);
        throwIfNotSuccess(response);
        const dtos: TemplateDto[] = await response.json();
        return dtos.map((x) => mapDtoToTemplate(x));
    }

    async function getTemplateProfile(
        type: TemplateType
    ): Promise<TemplateProfile> {
        const url = `${API_URL}/${type}`;
        const response = await authFetch(url);
        throwIfNotSuccess(response);
        const dtos: TemplateProfileDto = await response.json();
        return mapDtoToTemplateProfile(dtos);
    }

    async function newDraft(type: TemplateType): Promise<TemplateProfile> {
        const url = `${API_URL}/${type}/new-draft`;
        const response = await authFetch(url, {
            method: "POST"
        });
        throwIfNotSuccess(response);
        const dtos = await response.json();
        return mapDtoToTemplateProfile(dtos);
    }

    async function copyToDraft(
        type: TemplateType,
        templateId: number
    ): Promise<TemplateProfile> {
        const url = `${API_URL}/${type}/copy-to-draft/${templateId}`;
        const response = await authFetch(url, {
            method: "POST"
        });
        throwIfNotSuccess(response);
        const dtos = await response.json();
        return mapDtoToTemplateProfile(dtos);
    }

    async function updateDraft(
        type: TemplateType,
        content: string
    ): Promise<TemplateProfile> {
        const url = `${API_URL}/${type}/draft`;
        const payload = {
            content
        };

        const response = await authFetch(url, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload)
        });

        throwIfNotSuccess(response);
        const dto = await response.json();
        return mapDtoToTemplateProfile(dto);
    }

    async function publishDraft(type: TemplateType): Promise<TemplateProfile> {
        const url = `${API_URL}/${type}/publish-draft`;
        const response = await authFetch(url, {
            method: "POST"
        });
        throwIfNotSuccess(response);
        const dtos = await response.json();
        return mapDtoToTemplateProfile(dtos);
    }

    return {
        getReplacementFields,
        getTypes,
        getAllTemplateProfiles,
        getTemplateProfile,
        newDraft,
        updateDraft,
        publishDraft,
        copyToDraft,
        getPublishedWithReplacedFields
    } as TemplateService;
}

export interface TemplateService {
    getReplacementFields(): Promise<ReplacementField[]>;

    getTypes(): Promise<TemplateTypeDisplay[]>;

    getAllTemplateProfiles(): Promise<TemplateProfile[]>;

    getTemplateProfile(type: TemplateType): Promise<TemplateProfile>;

    copyToDraft(
        type: TemplateType,
        templateId: number
    ): Promise<TemplateProfile>;

    newDraft(selectedType: TemplateType): Promise<TemplateProfile>;

    updateDraft(
        selectedType: TemplateType,
        content: string
    ): Promise<TemplateProfile>;

    publishDraft(selectedType: TemplateType): Promise<TemplateProfile>;

    getPublishedWithReplacedFields(): Promise<Template[]>;
}
